import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, formatApiErrorDetail } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, CalendarPlus, Loader2, Save, FileText, Plus, ChevronDown, X } from "lucide-react";
import { format, addDays, startOfWeek } from "date-fns";

const AddShift = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [positions, setPositions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [weekDays, setWeekDays] = useState([]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [positionsDropdownOpen, setPositionsDropdownOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    event_name: "",
    start_time: "",
    end_time: "",
    days: [],
    assigned_to: "",
    positions_needed: [],
    slots: 1,
  });

  useEffect(() => {
    fetchEmployees();
    fetchPositions();
    fetchTemplates();
    generateWeekDays();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, {
        headers: getAuthHeaders(),
        
      });
      setEmployees(response.data.filter((e) => e.role !== "admin"));
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await axios.get(`${API}/positions`, {
        headers: getAuthHeaders(),
        
      });
      setPositions(response.data);
    } catch (error) {
      console.error("Error fetching positions:", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/shift-templates`, {
        headers: getAuthHeaders(),
        
      });
      setTemplates(response.data);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const generateWeekDays = () => {
    const today = new Date();
    const start = startOfWeek(today, { weekStartsOn: 0 });
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = addDays(start, i);
      days.push({
        date: format(day, "yyyy-MM-dd"),
        dayName: format(day, "EEE"),
        dayNumber: format(day, "d"),
        fullDate: format(day, "MMM d"),
      });
    }
    setWeekDays(days);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDayToggle = (date) => {
    setFormData((prev) => {
      const currentDays = prev.days;
      if (currentDays.includes(date)) {
        return { ...prev, days: currentDays.filter((d) => d !== date) };
      } else {
        return { ...prev, days: [...currentDays, date] };
      }
    });
  };

  const handlePositionToggle = (positionName) => {
    setFormData((prev) => {
      const current = prev.positions_needed;
      if (current.includes(positionName)) {
        return { ...prev, positions_needed: current.filter((p) => p !== positionName) };
      } else {
        return { ...prev, positions_needed: [...current, positionName] };
      }
    });
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find((t) => t.id === templateId);
    if (template) {
      setFormData((prev) => ({
        ...prev,
        event_name: template.event_name,
        start_time: template.start_time,
        end_time: template.end_time,
        positions_needed: template.positions_needed || [],
        slots: template.slots || 1,
      }));
      toast.success(`Template "${template.name}" loaded`);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Please enter a template name");
      return;
    }
    if (!formData.event_name || !formData.start_time || !formData.end_time) {
      toast.error("Please fill in event name and times first");
      return;
    }

    setSavingTemplate(true);
    try {
      await axios.post(
        `${API}/shift-templates`,
        {
          name: templateName,
          event_name: formData.event_name,
          start_time: formData.start_time,
          end_time: formData.end_time,
          positions_needed: formData.positions_needed,
          slots: formData.slots,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Template saved successfully!");
      setShowTemplateDialog(false);
      setTemplateName("");
      fetchTemplates();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.days.length === 0) {
      toast.error("Please select at least one day");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        assigned_to: formData.assigned_to || null,
      };
      await axios.post(`${API}/shifts`, payload, {
        headers: getAuthHeaders(),
        
      });
      toast.success(`${formData.days.length} shift(s) created successfully!`);
      navigate("/company-schedule");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to create shifts");
    } finally {
      setLoading(false);
    }
  };

  // Format time for display (military/24-hour)
  const formatMilitaryTime = (time) => {
    if (!time) return "";
    return time; // Already in HH:MM format
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="add-shift-page">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="text-[#666666] hover:text-[#1A1A1A]"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1A1A]">Add A Shift</h1>
          <p className="text-[#666666]">Schedule a new tour or shift</p>
        </div>
      </div>

      {/* Templates Section */}
      {templates.length > 0 && (
        <Card className="border border-[#E5E5E5] shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold uppercase tracking-wider text-[#666666] flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Load from Template
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {templates.map((template) => (
                <Button
                  key={template.id}
                  variant="outline"
                  size="sm"
                  onClick={() => handleTemplateSelect(template.id)}
                  className="border-[#E5E5E5] hover:border-[#6A1E2E] hover:bg-[#F0EAEB]"
                  data-testid={`template-${template.id}`}
                >
                  {template.name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border border-[#E5E5E5] shadow-none max-w-2xl">
        <CardHeader className="pb-4 flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-[#1A1A1A]">
            <CalendarPlus className="w-5 h-5 text-[#6A1E2E]" />
            Shift Details
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowTemplateDialog(true)}
            className="border-[#E5E5E5]"
            data-testid="save-template-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            Save as Template
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="add-shift-form">
            {/* Event Name */}
            <div className="space-y-2">
              <Label htmlFor="event_name">Event Name</Label>
              <Input
                id="event_name"
                name="event_name"
                value={formData.event_name}
                onChange={handleInputChange}
                placeholder="e.g., Morning Tour, Night Security"
                required
                className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                data-testid="input-event-name"
              />
            </div>

            {/* Time Row - Military Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_time">Shift Start Time (Military)</Label>
                <Input
                  id="start_time"
                  name="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={handleInputChange}
                  required
                  className="border-[#E5E5E5] focus:border-[#6A1E2E] font-mono"
                  data-testid="input-start-time"
                />
                <p className="text-xs text-[#999999]">
                  {formData.start_time && `${formData.start_time} hrs`}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_time">Shift End Time (Military)</Label>
                <Input
                  id="end_time"
                  name="end_time"
                  type="time"
                  value={formData.end_time}
                  onChange={handleInputChange}
                  required
                  className="border-[#E5E5E5] focus:border-[#6A1E2E] font-mono"
                  data-testid="input-end-time"
                />
                <p className="text-xs text-[#999999]">
                  {formData.end_time && `${formData.end_time} hrs`}
                </p>
              </div>
            </div>

            {/* Positions Needed - Dropdown Multi-select */}
            <div className="space-y-2">
              <Label>Positions Needed for This Shift</Label>
              <DropdownMenu open={positionsDropdownOpen} onOpenChange={setPositionsDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-between border-[#E5E5E5] hover:bg-[#FAFAFA]"
                    data-testid="positions-dropdown"
                  >
                    <span className="text-[#666666]">
                      {formData.positions_needed.length === 0
                        ? "Select positions..."
                        : `${formData.positions_needed.length} position(s) selected`}
                    </span>
                    <ChevronDown className="w-4 h-4 text-[#999999]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[300px] max-h-[300px] overflow-y-auto">
                  {positions.length === 0 ? (
                    <div className="p-3 text-sm text-[#999999]">
                      No positions available. Add positions in Settings.
                    </div>
                  ) : (
                    positions.map((pos) => (
                      <DropdownMenuCheckboxItem
                        key={pos.id}
                        checked={formData.positions_needed.includes(pos.name)}
                        onCheckedChange={() => handlePositionToggle(pos.name)}
                        data-testid={`position-needed-${pos.name.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {pos.name}
                      </DropdownMenuCheckboxItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Selected Positions Tags */}
              {formData.positions_needed.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.positions_needed.map((pos) => (
                    <span
                      key={pos}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-[#6A1E2E] text-white text-sm rounded-sm"
                    >
                      {pos}
                      <button
                        type="button"
                        onClick={() => handlePositionToggle(pos)}
                        className="hover:bg-white/20 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Number of Slots */}
            <div className="space-y-2">
              <Label htmlFor="slots">Number of People Needed</Label>
              <Input
                id="slots"
                name="slots"
                type="number"
                min="1"
                max="20"
                value={formData.slots}
                onChange={handleInputChange}
                className="border-[#E5E5E5] focus:border-[#6A1E2E] w-24"
                data-testid="input-slots"
              />
            </div>

            {/* Assign To */}
            <div className="space-y-2">
              <Label>Assign To (Optional)</Label>
              <Select
                value={formData.assigned_to}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, assigned_to: value === "open" ? "" : value }))
                }
              >
                <SelectTrigger className="border-[#E5E5E5]" data-testid="select-employee">
                  <SelectValue placeholder="Leave open or assign to employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open Shift</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                      {emp.positions?.length > 0 && (
                        <span className="text-xs text-[#999999] ml-2">
                          ({emp.positions.join(", ")})
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Day Selection */}
            <div className="space-y-3">
              <Label>Select Days</Label>
              <p className="text-sm text-[#666666]">
                Current week: {weekDays.length > 0 && `${weekDays[0].fullDate} - ${weekDays[6].fullDate}`}
              </p>
              <div className="grid grid-cols-7 gap-2">
                {weekDays.map((day) => (
                  <button
                    key={day.date}
                    type="button"
                    onClick={() => handleDayToggle(day.date)}
                    className={`flex flex-col items-center p-3 rounded-sm border transition-colors ${
                      formData.days.includes(day.date)
                        ? "bg-[#6A1E2E] text-white border-[#6A1E2E]"
                        : "bg-white text-[#1A1A1A] border-[#E5E5E5] hover:border-[#6A1E2E]"
                    }`}
                    data-testid={`day-${day.dayName.toLowerCase()}`}
                  >
                    <span className="text-xs font-semibold uppercase">{day.dayName}</span>
                    <span className="text-lg font-bold">{day.dayNumber}</span>
                  </button>
                ))}
              </div>
              {formData.days.length > 0 && (
                <p className="text-sm text-[#6A1E2E]">
                  {formData.days.length} day(s) selected
                </p>
              )}
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="border-[#E5E5E5]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
                data-testid="submit-shift"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Shift(s)"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Save Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Save as Template</DialogTitle>
            <DialogDescription>
              Save this shift configuration as a reusable template.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="template_name">Template Name</Label>
              <Input
                id="template_name"
                placeholder="e.g., Morning Tour Standard"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                data-testid="template-name-input"
              />
            </div>
            <div className="text-sm text-[#666666] p-3 bg-[#FAFAFA] rounded-sm">
              <p><strong>Event:</strong> {formData.event_name || "Not set"}</p>
              <p><strong>Time:</strong> {formData.start_time || "--:--"} - {formData.end_time || "--:--"}</p>
              <p><strong>Positions:</strong> {formData.positions_needed.join(", ") || "None"}</p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowTemplateDialog(false)}
              className="border-[#E5E5E5]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveTemplate}
              disabled={savingTemplate}
              className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
              data-testid="confirm-save-template"
            >
              {savingTemplate ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddShift;
